import { Resizable } from "re-resizable"
import { useCallback, useEffect, useState } from "react"
import { ImageSettingsForm, FloatingImageEditor } from "../FloatingImageEditor/FloatingImageEditor"
import { useTextEditorContext } from "@web/components/TextEditor/TextEditor.context"
import { Dimensions, useResizeObserver } from "@web/hooks/useResizeObserver"
import { ImageOverlayText } from "./ImageOverlayText/ImageOverlayText"

interface Props {
  className: string | null
  imageRef: { current: null | HTMLImageElement }
  src: string
  link?: string
  width?: number
  height?: number
  isRounded?: boolean
  fileName?: string
  fileType?: string
  topText?: string
  topTextColor?: string
  bottomText?: string
  bottomTextColor?: string

  onImageSettingsChange?: (settings: ImageSettingsForm) => void
}

export const RESIZE_HANDLE_WRAPPER_CLASS = "resize-handle-wrapper"

export const EditableImage = ({
  className,
  imageRef,
  src,
  link,
  isRounded,
  width: width_ = 200,
  fileName,
  fileType,
  topText,
  topTextColor,
  bottomText,
  bottomTextColor,

  onImageSettingsChange,
}: Props) => {
  const { inputWidth } = useTextEditorContext()
  const [isEditingImage, setIsEditingImage] = useState<HTMLElement | null>(null)
  const [imageWidth, setImageWidth] = useState(width_)
  const { height: imageHeight } = useResizeObserver(imageRef)

  const [imageSettings, setImageSettings] = useState<ImageSettingsForm>({
    isRounded: isRounded || false,
    link: link || "",
    topText: topText ?? "",
    topTextColor: topTextColor || "white",
    bottomText: bottomText ?? "",
    bottomTextColor: bottomTextColor || "white",
  })

  const handleResize = useCallback((_: unknown, __: unknown, e: HTMLElement) => {
    setImageWidth(e.clientWidth)
  }, [])

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsEditingImage(e.currentTarget)
  }, [])

  useEffect(
    function handleImageSettingsChange() {
      onImageSettingsChange?.({ ...imageSettings, width: imageWidth, height: imageHeight })
    },
    [imageSettings, onImageSettingsChange, imageWidth, imageHeight]
  )

  const image = (
    <ResizableImage
      width={imageWidth}
      maxWidth={inputWidth || 500}
      src={src}
      imageRef={imageRef}
      link={link}
      fileName={fileName}
      fileType={fileType}
      topText={topText}
      topTextColor={topTextColor}
      bottomText={bottomText}
      bottomTextColor={bottomTextColor}
      isRounded={isRounded || false}
      className={className}
      onResize={handleResize}
    />
  )

  return (
    <>
      <div onClick={handleClick} style={{ minHeight: imageHeight }}>
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" draggable={false}>
            {image}
          </a>
        ) : (
          image
        )}
      </div>

      {isEditingImage && (
        <FloatingImageEditor
          onDone={() => setIsEditingImage(null)}
          element={imageRef.current!}
          onSettingsChange={setImageSettings}
        />
      )}
    </>
  )
}

interface ResizableImageProps extends Props {
  width: number
  maxWidth: number

  onResize: (_: unknown, __: unknown, e: HTMLElement) => void
}

const ResizableImage = ({
  width,
  maxWidth,
  src,
  imageRef,
  link,
  fileName,
  fileType,
  topText,
  topTextColor,
  bottomText,
  bottomTextColor,
  isRounded,
  className,
  onResize,
}: ResizableImageProps) => {
  const _topText = topText?.trim()
  const _bottomText = bottomText?.trim()

  const { height: imageHeight } = useResizeObserver(imageRef)
  const [topTextDimensions, setTopTextDimensions] = useState<Dimensions>({ width: 0, height: 0 })

  const hasTextOverlay = Boolean(_topText || _bottomText)

  return (
    <Resizable
      defaultSize={{ width, height: "auto" }}
      lockAspectRatio
      onResize={onResize}
      maxWidth={maxWidth}
      handleWrapperClass={RESIZE_HANDLE_WRAPPER_CLASS}
      style={{ position: "relative" }}
    >
      <img
        className={className || undefined}
        src={src}
        ref={imageRef}
        data-link={link}
        data-file-name={fileName}
        data-file-type={fileType}
        data-top-text={topText}
        data-top-text-color={topTextColor}
        data-bottom-text={bottomText}
        data-bottom-text-color={bottomTextColor}
        data-height={imageHeight}
        style={{ maxWidth: "100%", borderRadius: isRounded ? "50%" : undefined, width }}
        draggable="false"
      />

      {hasTextOverlay ? (
        <div
          style={{ margin: `-${imageHeight}px 0 0 0`, display: "flex", flexDirection: "column" }}
          data-text-container
        >
          {_topText && (
            <ImageOverlayText
              type="top"
              textColor={topTextColor}
              imageHeight={imageHeight}
              text={_topText}
              onDimensionsChange={setTopTextDimensions}
            />
          )}

          {_bottomText && (
            <ImageOverlayText
              type="bottom"
              textColor={bottomTextColor}
              imageHeight={imageHeight}
              text={_bottomText}
              topOffset={_topText ? topTextDimensions.height : 0}
            />
          )}
        </div>
      ) : null}
    </Resizable>
  )
}
